import React from 'react'
import { makeStyles } from '@material-ui/core/styles';

// Components
import Alignment from './components/Alignment/Alignment';
import TopRightButtons from './components/TopRightButtons/TopRightButtons';
import Formats from './components/Formats/Formats';
import FontSize from './components/FontSize/FontSize';
import FontColor from './components/FontColor/FontColor';
import Highlighter from './components/Highlighter/Highlighter';

// Material UI Components
import { Popover, Fade, Grid, Toolbar, Divider, Typography} from '@material-ui/core';

// Material UI Custom Style
const useStyles = makeStyles(theme => ({
  Paper: {
    width: "auto",
    height: "auto",
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.default,
  },
  Toolbar: {
    width: "100%",
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  Button: {
    maxWidth: "72px",
    minWidth: "44px",
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    boxShadow: "none",
    '&:hover': {
      boxShadow: "none",
    }
  },
  Divider: {
    alignSelf: 'stretch',
    margin: theme.spacing(0, 2)
  },
  Caption: {
    textAlign: 'left',
    fontSize: "12px",
    color: "rgba(0,0,0,0.87)",
    margin: theme.spacing(1, 1.5, 0, 0),
  },
  ColorContainer: {
    width: "102px",
    display: 'flex',
    flexWrap: 'wrap',
    margin: theme.spacing(1, 0)
  }
}));

export default function InlineToolbar( {editorState, onClickToggleBlockStyle, onClickToggleStyle, handleCursorSetAtEnd, onClickRemoveLastFormat, onClickToggleLink }) {
  const Style = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [newtimeoutCount, setNewtimeoutCount] = React.useState(null);
  const [offset, setOffset] = React.useState(null);

  const handleCloseAnchorEL = () => {
      setAnchorEl(null);
  };

  let open = Boolean(anchorEl);

  const newEditorState = editorState;
  const selectionState = newEditorState.getSelection();
  const currentSelectionCollapsed = selectionState.isCollapsed();
  const currentSelectionIsBackward = selectionState.getIsBackward();
  const currentSelectionAnchorKey = selectionState.getAnchorKey();
  const currentSelectionAnchorOffset = selectionState.getAnchorOffset();
  const currentSelectionFocusOffset = selectionState.getFocusOffset();
  
  const contentState = newEditorState.getCurrentContent();
  const contentBlock = contentState.getBlockForKey(currentSelectionAnchorKey);
  const contentBlockLength = contentBlock.getLength();
  
  const selectionStateStart = selectionState.getStartOffset();
  const selectionStateEnd = selectionState.getEndOffset();

  const timeoutCount = Math.round((currentSelectionFocusOffset + selectionStateStart) * (currentSelectionFocusOffset + selectionStateEnd)) / (contentBlockLength / 10);
  
  React.useEffect(() => {
    if(timeoutCount !== newtimeoutCount){
      setTimeout(() => {
        setNewtimeoutCount(timeoutCount); 
      }, 1000);
    } else if(timeoutCount === newtimeoutCount){
      if((currentSelectionFocusOffset > selectionStateStart || currentSelectionFocusOffset < selectionStateEnd) && currentSelectionCollapsed === false){

        const childElements = Array.from(document.querySelectorAll('.public-DraftStyleDefault-block'));

        const parentElement = childElements.find(item => {
          const key = item.dataset.offsetKey;
          const keyArray = key.split('-');
          const newKey = keyArray[0]

          return (newKey === currentSelectionAnchorKey)
        })

        setAnchorEl(parentElement)
      } 

      if(currentSelectionIsBackward === true){
        setOffset(currentSelectionFocusOffset)
      }else{
        setOffset(currentSelectionAnchorOffset)
      }
    }
  }, [timeoutCount, newtimeoutCount, currentSelectionFocusOffset, selectionStateStart, selectionStateEnd, currentSelectionCollapsed, currentSelectionIsBackward, currentSelectionAnchorKey, setAnchorEl, currentSelectionAnchorOffset])

  return (
    <Popover
      open={open}
      onClose={handleCloseAnchorEL}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onExiting={() => handleCursorSetAtEnd(null)}
      elevation={0}
      marginThreshold={0}
      TransitionComponent={Fade}
      transitionDuration={1000}
    >
    <Grid 
      container 
      className={Style.Paper}
      direction="column"
      justify="space-between"
      alignItems="center"
    >
      <Grid item>
        <Toolbar >
          <Alignment StyleButton={Style.Button} contentBlock={contentBlock} onClickToggleBlockStyle={onClickToggleBlockStyle} />
          <TopRightButtons StyleButton={Style.Button} contentBlock={contentBlock} anchorKey={currentSelectionAnchorKey} currentSelectionOffset={offset} onClickRemoveLastFormat={onClickRemoveLastFormat} onClickToggleLink={onClickToggleLink} />
        </Toolbar>
      </Grid>
      <Divider variant="middle" className={Style.Divider} />
      <Grid item>
        <Toolbar>
          <Formats StyleButton={Style.Button} currentSelectionOffset={offset} contentBlock={contentBlock} onClickToggleStyle={onClickToggleStyle} />
        </Toolbar>
      </Grid>
      <Divider variant="middle" className={Style.Divider} />
      <Grid item>
        <Toolbar className={Style.Toolbar} variant="dense">
          <Typography variant="button" className={Style.Caption}>Font Size:</Typography>
          <Toolbar disableGutters variant="dense">
            <FontSize StyleButton={Style.Button} currentSelectionOffset={offset} contentBlock={contentBlock} onClickToggleStyle={onClickToggleStyle} />
          </Toolbar>
        </Toolbar>
      </Grid>
      <Divider variant="middle" className={Style.Divider}  />
      <Grid item style={{width: '270px'}}>
        <Toolbar className={Style.Toolbar} variant="dense">
          <Grid container alignItems="center" justify="space-between">
            <Grid item style={{marginTop: "10px"}}>
              <Typography variant="button" className={Style.Caption}>Font Color:</Typography>
              <div className={Style.ColorContainer}>
                <FontColor StyleButton={Style.Button} currentSelectionOffset={offset} contentBlock={contentBlock} onClickToggleStyle={onClickToggleStyle} />
              </div>
            </Grid>
            <Grid item style={{marginTop: "10px"}}>
              <Typography variant="button" className={Style.Caption}>Highlighter:</Typography>
              <div className={Style.ColorContainer}>
                <Highlighter StyleButton={Style.Button} currentSelectionOffset={offset} contentBlock={contentBlock} onClickToggleStyle={onClickToggleStyle} />              
              </div>
            </Grid>
          </Grid>
        </Toolbar>
      </Grid>
    </Grid>
  </Popover>
)}